import { useEffect, useMemo, useState } from 'react';
import { Button, Dropdown, InlineNotification } from '@carbon/react';
import { Download } from '@carbon/icons-react';
import {
  listFirmwareReleasesForGateway,
  scheduleGatewayOta,
} from '../../../services/espGatewayOtaService';
import { getGatewayHardwareType } from '../../../utils/gatewayProductLabel';

/**
 * ESP (LGateway / MGateway) OTA — pick a published firmware_releases row and schedule OTA
 * (device polls lilygo-check-ota). Matches ble_frontend “Schedule OTA”.
 */
export default function EspFirmwareOtaPanel({ gateway, disabled, onScheduled }) {
  const [releases, setReleases] = useState([]);
  const [loading, setLoading] = useState(false);
  const [scheduling, setScheduling] = useState(false);
  const [error, setError] = useState('');
  const [selectedRelease, setSelectedRelease] = useState(null);

  const hardwareType = useMemo(() => getGatewayHardwareType(gateway), [gateway]);

  useEffect(() => {
    if (!gateway?.id) return undefined;
    let cancelled = false;
    setLoading(true);
    setError('');
    listFirmwareReleasesForGateway(gateway)
      .then((result) => {
        if (cancelled) return;
        if (!result.success) {
          setError(result.error || 'Could not load firmware releases');
          setReleases([]);
        } else {
          setReleases(result.releases);
          setSelectedRelease(result.releases[0] || null);
        }
        setLoading(false);
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err.message || 'Could not load firmware releases');
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [gateway?.id, hardwareType]);

  const releaseItems = useMemo(
    () =>
      releases.map((r) => ({
        id: r.id,
        label: `${r.version} [${r.channel || 'stable'}]`,
        release: r,
      })),
    [releases],
  );

  const handleSchedule = async () => {
    if (!selectedRelease?.release) return;
    setScheduling(true);
    setError('');
    const result = await scheduleGatewayOta(gateway, selectedRelease.release);
    setScheduling(false);
    if (!result.success) {
      setError(result.error || 'Failed to schedule OTA');
      return;
    }
    onScheduled?.(result);
  };

  return (
    <div className="gateway-admin-ota space-y-3">
      <p className="text-xs text-[#525252] leading-relaxed">
        Select a published release from Supabase <code className="text-xs bg-gray-100 px-1 rounded">firmware_releases</code>{' '}
        ({hardwareType}). The gateway polls OTA when online — same as ble_frontend Schedule OTA.
      </p>

      {error && (
        <InlineNotification kind="error" title="OTA" subtitle={error} hideCloseButton lowContrast />
      )}

      {loading ? (
        <p className="text-sm text-[#525252]">Loading firmware releases…</p>
      ) : releaseItems.length === 0 ? (
        <p className="text-sm text-[#525252]">
          No published releases for <strong>{hardwareType}</strong>. Publish a build via ble_frontend fleet admin first.
        </p>
      ) : (
        <>
          <div className="command-dropdown-wrapper gateway-admin-dropdown">
            <Dropdown
              id="esp-firmware-release"
              titleText="Firmware release"
              label="Select release"
              items={releaseItems}
              itemToString={(item) => (item ? item.label : '')}
              selectedItem={selectedRelease}
              onChange={({ selectedItem }) => setSelectedRelease(selectedItem)}
              disabled={disabled || scheduling}
              size="md"
            />
          </div>
          <Button
            kind="primary"
            className="gateway-admin-action-btn w-full"
            renderIcon={Download}
            onClick={handleSchedule}
            disabled={disabled || scheduling || !selectedRelease}
          >
            {scheduling ? 'Scheduling…' : 'Schedule OTA'}
          </Button>
        </>
      )}
    </div>
  );
}
